import React, { useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { uploadAttachment } from "@/lib/api";
import { uploadErrorText } from "@/lib/imageUpload";
import ImageInput from "@/components/ImageInput";
import LocationPinPicker from "@/components/maps/LocationPinPicker";
import MultiImageInput from "@/components/MultiImageInput";
import { MicRow, appendText } from "@/components/VoiceMic";

// Labels are [English, Hindi]. Field keys/validation are unchanged.
const SEL = ["— Select —", "— चुनें —"];
const UNITS = [["per visit", "Per visit", "प्रति विज़िट"], ["per hour", "Per hour", "प्रति घंटा"], ["per consultation", "Per consultation", "प्रति परामर्श"], ["per day", "Per day", "प्रति दिन"], ["per job", "Per job", "प्रति काम"], ["fixed", "Fixed price", "तय कीमत"]];

export const ACTIVITY_FORMS = {
  professional: [
    { k: "name", l: ["Professional / business name", "पेशेवर / व्यवसाय का नाम"], ph: ["e.g. Sharma Legal Services", "जैसे: शर्मा लीगल सर्विसेज़"] },
    { k: "profession", l: ["Profession", "पेशा"], ph: ["e.g. Lawyer, Tutor, Electrician", "जैसे: वकील, ट्यूटर, इलेक्ट्रीशियन"] },
    { k: "category", l: ["Category", "श्रेणी"] },
    { k: "description", l: ["Service description", "सेवा का विवरण"], type: "textarea", ph: ["What do you offer?", "आप कौन-सी सेवा देते हैं?"] },
    { k: "specialties", l: ["Specialties", "विशेषज्ञता"] },
    { k: "address", l: ["Office address", "ऑफ़िस का पता"] },
    { k: "locality", l: ["Locality", "मोहल्ला / इलाका"] },
    { k: "pincode", l: ["PIN code", "पिन कोड"], type: "pin", ph: ["6 digits", "6 अंक"] },
    { k: "service_area", l: ["Service area (areas / PINs you cover)", "सेवा क्षेत्र (इलाके / पिन)"] },
    { k: "loc_lat", l: ["Service location on map (optional)", "मैप पर सेवा स्थान (वैकल्पिक)"], type: "geo" },
    { k: "share_exact_location", l: ["Customers ko exact location se directions dein (warna public map pe sirf lagbhag ~200 m area dikhega)", "ग्राहकों को सटीक लोकेशन से दिशा दिखाएँ (वरना सिर्फ़ ~200 मी. का अनुमानित क्षेत्र)"], type: "toggle" },
    { k: "fee_mode", l: ["Fee", "फ़ीस"], type: "select", options: [["", ...SEL], ["fixed", "Fixed fee / price", "तय फ़ीस / कीमत"], ["quote", "Custom quotation / Fee as discussed", "बातचीत से तय फ़ीस / कोटेशन"]] },
    { k: "fee", l: ["Fee amount (₹)", "फ़ीस राशि (₹)"], type: "number", show: (d) => d.fee_mode === "fixed" },
    { k: "pricing_unit", l: ["Pricing unit", "फ़ीस किस आधार पर"], type: "select", options: [["", ...SEL], ...UNITS], show: (d) => d.fee_mode === "fixed" },
    { k: "availability", l: ["Availability", "उपलब्धता"], ph: ["e.g. Mon–Sat, 10am–6pm", "जैसे: सोम–शनि, सुबह 10 से शाम 6"] },
    { k: "appointments", l: ["I take appointments", "मैं अपॉइंटमेंट लेता/लेती हूँ"], type: "toggle" },
    { k: "photo", l: ["Profile photo", "प्रोफ़ाइल फ़ोटो"], type: "image", lib: "professional" },
    { k: "gallery", l: ["Gallery photos", "गैलरी फ़ोटो"], type: "images", lib: "professional" },
    { k: "qualification", l: ["Qualification (shown as self-declared)", "योग्यता (स्व-घोषित दिखेगी)"] },
    { k: "registration_no", l: ["Registration / licence no. (private)", "रजिस्ट्रेशन / लाइसेंस नंबर (निजी)"] },
    { k: "documents", l: ["Supporting documents (private — only you and admin)", "सहायक दस्तावेज़ (निजी — सिर्फ़ आप और एडमिन)"], type: "files" },
    { k: "phone", l: ["Call number", "कॉल नंबर"], type: "phone" },
    { k: "whatsapp", l: ["WhatsApp number", "WhatsApp नंबर"], type: "phone" },
  ],
  employer: [
    { k: "org_name", l: ["Employer / organisation name", "नियोक्ता / संस्था का नाम"] },
    { k: "about", l: ["About the organisation", "संस्था के बारे में"], type: "textarea" },
    { k: "address", l: ["Address", "पता"] },
    { k: "pincode", l: ["PIN code", "पिन कोड"], type: "pin", ph: ["6 digits", "6 अंक"] },
    { k: "phone", l: ["Phone", "फ़ोन"], type: "phone" },
    { k: "whatsapp", l: ["WhatsApp", "WhatsApp"], type: "phone" },
    { k: "email", l: ["Email", "ईमेल"] },
  ],
  job_seeker: [
    { k: "name", l: ["Name", "नाम"] },
    { k: "skills", l: ["Skills", "कौशल / हुनर"], type: "textarea" },
    { k: "qualifications", l: ["Qualifications", "शैक्षिक योग्यता"] },
    { k: "experience", l: ["Experience", "अनुभव"] },
    { k: "preferred_work", l: ["Preferred work", "पसंदीदा काम"] },
    { k: "preferred_pincode", l: ["Preferred work PIN", "काम के लिए पसंदीदा पिन"], type: "pin", ph: ["6 digits", "6 अंक"] },
    { k: "contact_pref", l: ["Contact preference", "संपर्क का तरीका"], type: "select", options: [["", ...SEL], ["phone", "Phone call", "फ़ोन कॉल"], ["whatsapp", "WhatsApp", "WhatsApp"], ["email", "Email", "ईमेल"]] },
    { k: "phone", l: ["Phone (private unless you allow below)", "फ़ोन (नीचे अनुमति देने तक निजी)"], type: "phone" },
    { k: "email", l: ["Email (private unless you allow below)", "ईमेल (नीचे अनुमति देने तक निजी)"] },
    { k: "contact_public", l: ["Show my phone/email on my public profile", "मेरा फ़ोन/ईमेल सार्वजनिक प्रोफ़ाइल पर दिखाएँ"], type: "toggle" },
    { k: "documents", l: ["Resume / certificates (private)", "बायोडाटा / प्रमाणपत्र (निजी)"], type: "files" },
  ],
  other: [
    { k: "activity_name", l: ["Activity or business name", "गतिविधि या व्यवसाय का नाम"] },
    { k: "activity_type", l: ["Type of activity", "गतिविधि का प्रकार"] },
    { k: "category", l: ["Category (existing)", "श्रेणी (मौजूदा)"] },
    { k: "new_category", l: ["Suggest a new category (sent for admin review)", "नई श्रेणी सुझाएँ (एडमिन जाँच करेंगे)"] },
    { k: "description", l: ["Description", "विवरण"], type: "textarea" },
    { k: "pincode", l: ["PIN code", "पिन कोड"], type: "pin", ph: ["6 digits", "6 अंक"] },
    { k: "phone", l: ["Phone", "फ़ोन"], type: "phone" },
    { k: "whatsapp", l: ["WhatsApp", "WhatsApp"], type: "phone" },
    { k: "pricing", l: ["Pricing or quotation details", "कीमत या कोटेशन की जानकारी"] },
    { k: "photos", l: ["Photos", "फ़ोटो"], type: "images", lib: "other_services" },
  ],
};

const UI = {
  save: ["Save (finish later)", "सेव करें (बाद में पूरा करें)"],
  publish: ["Save & Publish", "सेव करें और प्रकाशित करें"],
  unpublish: ["Unpublish", "प्रकाशन हटाएँ"],
  attach: ["Upload File", "फ़ाइल अपलोड करें"],
  docHint: ["PDF, DOC/DOCX, JPG or PNG · max 10 MB", "PDF, DOC/DOCX, JPG या PNG · अधिकतम 10 MB"],
};
const HI_ERRORS = {
  "PIN must be 6 digits": "पिन कोड 6 अंकों का होना चाहिए",
  "Invalid phone number": "फ़ोन नंबर सही नहीं है",
  "Invalid whatsapp number": "WhatsApp नंबर सही नहीं है",
  "fee must be a number": "फ़ीस एक संख्या होनी चाहिए",
  "fee out of range": "फ़ीस की राशि सही नहीं है",
};
export const errorIn = (msg, li) => (li === 1 && HI_ERRORS[msg]) || msg;
export const useFormLang = () => {
  const { i18n } = useTranslation();
  return useState(i18n.language === "hi" ? 1 : 0);
};
export const LangToggle = ({ li, setLi, testid }) => (
  <div className="inline-flex rounded-full border border-slate-200 p-0.5 text-[11px] font-semibold" data-testid={testid}>
    {["English", "हिन्दी"].map((l, i) => (
      <button key={l} type="button" onClick={() => setLi(i)} className={`px-2.5 py-1 rounded-full ${li === i ? "bg-emerald-700 text-white" : "text-slate-600"}`} data-testid={`${testid}-${i ? "hi" : "en"}`}>{l}</button>
    ))}
  </div>
);

const inputCls = "w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500";

function FilesField({ value, names, onChange, testid, li }) {
  const [busy, setBusy] = useState(false);
  const list = value || [];
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || busy) return;
    setBusy(true);
    try {
      const up = await uploadAttachment(f, "document");
      onChange([...list, up.file_id].slice(0, 10), { [up.file_id]: up.filename });
      toast.success(`${up.filename} uploaded`);
    } catch (err) { toast.error(uploadErrorText(err)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <label className={`inline-flex items-center gap-1.5 rounded-full border border-slate-200 hover:border-emerald-500 px-3 py-1.5 text-xs font-semibold cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid={testid}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} {UI.attach[li]}
        <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={pick} className="hidden" data-testid={`${testid}-input`} />
      </label>
      <div className="text-[11px] text-slate-400 mt-1">{UI.docHint[li]}</div>
      {list.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {list.map((id) => (
            <li key={id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2 py-1">
              <Paperclip className="w-3 h-3 text-slate-400" /><span className="flex-1 truncate">{names[id] || "Document"}</span>
              <button type="button" onClick={() => onChange(list.filter((x) => x !== id))} aria-label="Remove"><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ f, data, set, kind, names, setNames, li }) {
  const v = data[f.k];
  const tid = `af-${kind}-${f.k}`;
  const ph = f.ph ? f.ph[li] : undefined;
  if (f.type === "textarea") return (<><textarea value={v || ""} onChange={(e) => set(f.k, e.target.value)} rows={3} maxLength={2000} placeholder={ph} className={inputCls} data-testid={tid} /><MicRow onText={(t) => set(f.k, appendText(v || "", t).slice(0, 2000))} testid={`mic-${tid}`} /></>);
  if (f.type === "select") return <select value={v || ""} onChange={(e) => set(f.k, e.target.value)} className={inputCls} data-testid={tid}>{f.options.map((o) => <option key={o[0]} value={o[0]}>{o[1 + li]}</option>)}</select>;
  if (f.type === "toggle") return <input type="checkbox" checked={!!v} onChange={(e) => set(f.k, e.target.checked)} className="w-4 h-4 accent-emerald-700" data-testid={tid} />;
  if (f.type === "geo") return <LocationPinPicker lat={data.loc_lat} lng={data.loc_lng} onChange={(a, b) => { set("loc_lat", a); set("loc_lng", b); }} onClear={() => { set("loc_lat", ""); set("loc_lng", ""); }} testid={tid} />;
  if (f.type === "image") return <ImageInput value={v || ""} onChange={(x) => set(f.k, x)} label="" testid={tid} libraryCategory={f.lib} />;
  if (f.type === "images") return <MultiImageInput value={v || []} onChange={(x) => set(f.k, x.slice(0, 10))} label="" testid={tid} libraryCategory={f.lib} />;
  if (f.type === "files") return <FilesField li={li} value={v} names={names} onChange={(x, n) => { set(f.k, x); if (n) setNames((m) => ({ ...m, ...n })); }} testid={tid} />;
  const clean = f.type === "pin" ? (x) => x.replace(/\D/g, "").slice(0, 6) : f.type === "phone" ? (x) => x.replace(/[^\d+]/g, "").slice(0, 13) : (x) => x;
  return <input value={v ?? ""} type={f.type === "number" ? "number" : "text"} min={0} placeholder={ph} inputMode={f.type === "pin" || f.type === "phone" ? "numeric" : undefined} onChange={(e) => set(f.k, clean(e.target.value))} maxLength={f.type === "number" ? undefined : 300} className={inputCls} data-testid={tid} />;
}

export function ActivityForm({ kind, initial, onSave, busy }) {
  const [data, setData] = useState(initial || {});
  const [names, setNames] = useState({});
  const [li, setLi] = useFormLang();
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const save = (published) => { setData((d) => ({ ...d, published })); onSave({ ...data, published }, li); };
  const wide = (t) => ["textarea", "files", "images", "image"].includes(t);
  return (
    <div className="space-y-3" data-testid={`activity-form-${kind}`}>
      <div className="flex justify-end"><LangToggle li={li} setLi={setLi} testid={`form-lang-${kind}`} /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {ACTIVITY_FORMS[kind].filter((f) => !f.show || f.show(data)).map((f) => (
          <div key={f.k} className={`min-w-0 ${wide(f.type) ? "sm:col-span-2" : ""}`}>
            <label className={`text-[13px] font-bold text-slate-900 mb-1 ${f.type === "toggle" ? "inline-flex items-center gap-2 flex-row-reverse justify-end" : "block"}`} data-testid={`label-${kind}-${f.k}`}>
              {f.l[li]}
              {f.type === "toggle" && <Field f={f} data={data} set={set} kind={kind} names={names} setNames={setNames} li={li} />}
            </label>
            {f.type !== "toggle" && <Field f={f} data={data} set={set} kind={kind} names={names} setNames={setNames} li={li} />}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <button type="button" disabled={busy} onClick={() => save(!!data.published)} className="rounded-full border border-slate-200 hover:border-emerald-500 px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid={`activity-save-${kind}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : UI.save[li]}
        </button>
        <button type="button" disabled={busy} onClick={() => save(!data.published)} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid={`activity-publish-${kind}`}>
          {data.published ? UI.unpublish[li] : UI.publish[li]}
        </button>
      </div>
    </div>
  );
}
